import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DateUtils } from '@vectord/utils';
import { Icon, Popover } from '@vectord/components';
import { DayPicker } from 'react-day-picker';
import { useOutsideClick } from '@vectord/hooks';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import 'react-day-picker/dist/style.css';

import styles from './DateInput.module.scss';

const placementsMap = {
  bottom: { top: '110%', right: 0 },
  top: { bottom: '110%', right: 0 },
};

function DateInput({
  className,
  compact,
  isDisabled = false,
  onChange,
  pickerClassName,
  placeholder = 'Select a date',
  placement = 'bottom',
  value,
  ...remainingProps
}) {
  const [isOpen, setOpen] = useState(false);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  const selectedDate = useMemo(() => value && new Date(value), [value]);

  const handleToggle = useCallback(() => setOpen((prev) => !prev), []);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleChange = useCallback(
    (newDate) => {
      if (isDisabled) return;

      setOpen(false);
      onChange(newDate ? newDate.getTime() : null);
    },
    [isDisabled, onChange]
  );

  /**
   * Close popover when clicked outside of it.
   */
  const handleOutsideClick = useCallback(
    (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        handleClose();
      }
    },
    [handleClose]
  );

  useOutsideClick(inputRef, handleOutsideClick);

  return (
    <>
      <div
        ref={inputRef}
        className={classNames(
          styles.root,
          { [styles.compact]: compact },
          { [styles.compactDisabled]: isDisabled && compact },
          { [styles.disabled]: isDisabled && !compact },
          className
        )}
      >
        <div
          className={classNames(styles.value, {
            [styles.valueDisabled]: isDisabled,
          })}
        >
          {value ? (
            DateUtils.toDDMMMYYYY(value)
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        <Icon
          name="calendar"
          className={styles.control}
          onClick={handleToggle}
        />
      </div>
      <Popover
        ref={popoverRef}
        anchorRef={inputRef}
        className={styles.popover}
        isOpen={isOpen && !isDisabled}
        location={placementsMap[placement]}
        onClose={handleClose}
      >
        <DayPicker
          className={classNames(
            styles.picker,
            { [styles.compactPicker]: compact },
            pickerClassName
          )}
          defaultMonth={value && selectedDate}
          mode="single"
          selected={selectedDate}
          onSelect={handleChange}
          {...remainingProps}
        />
      </Popover>
    </>
  );
}

DateInput.propTypes = {
  className: PropTypes.string,
  compact: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  pickerClassName: PropTypes.string,
  placeholder: PropTypes.string,
  placement: PropTypes.oneOf(Object.keys(placementsMap)),
  value: PropTypes.number,
};

export default DateInput;
